import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Common, color, Widget } from '@stockbit/ui';

// alert error message
import handleErrorMessageAPI from 'global/AlertErrorMessage';

import valuationApi from 'lib/api/valuation';
import SUCCESS_MESSAGE from 'constants/successMessage';
import Search from '../Common/Search';
import ModalCreate from './ModalCreate';
import ValuationModalUpdate from './ModalUpdate';
import ValuationResult from './ValuationResult';

import { effects, selectors } from '../slice';
import InputMetrics from './Form/InputMetrics';
import InputMetricMultiple from './Form/InputMetricMultiple';
// constant

const { Text } = Common;
const { Button, Select } = Widget;

interface IStock {
  id: number;
  name: string;
}

const VALUATION_METRICS = {
  EPS: 'eps',
  GROWTH: 'growth',
  MULTIPLE: 'multiple',
};

interface ValuationFormTypes {
  isUpdateMode: boolean;
  setUpdateMode: (param: boolean) => void;
  isShowResultSection: boolean;
  setShowResultSection: (param: boolean) => void;
}

const ValuationForm = ({
  isUpdateMode,
  setUpdateMode,
  isShowResultSection,
  setShowResultSection,
}: ValuationFormTypes) => {
  const dispatch = useDispatch();
  const state = useSelector(selectors);
  const {
    postValuationResult,
    postValuationTemplateAdd,
    postValuationTemplateSave,
    getValuationMetricsDefault,
    getValuationTemplateLoad,
  } = state;

  const [updatedValuationId, setUpdatedValuationId] = useState(0);
  const [updatedValuationName, setUpdatedValuationName] = useState('');

  const [stock, setStock] = useState<IStock | null>(null);
  const [valueSelectEPS, setValueSelectEPS] = useState<number | string>(0);
  const [valueEPS, setValueEPS] = useState('');

  const [valueSelectGrowth, setValueSelectGrowth] =
    useState<number | string>('CUSTOM');
  const [valueGrowth, setValueGrowth] = useState('');

  const [valueSelectMultiple, setValueSelectMultiple] =
    useState<number | string>(0);
  const [valueMultiple, setValueMultiple] = useState('');

  const [isValuationModalCreate, setValuationModalCreate] = useState(false);
  const [isValuationModalUpdate, setValuationModalUpdate] = useState(false);
  const [inputedNotes, setInputedNotes] = useState('');

  const [isValuationMetricsDisabled, setValuationMetricsDisabled] =
    useState(true);

  const [seedDataValuationResult, setSeedDataValuationResult] = useState({
    target_price: '',
    current_price: '',
    margin_safety: '',

    consensus_high: '',
    consensus_low: '',
    consensus_medium: '',

    symbol: '',
  });

  const { isLoading: isLoadingPostValuationTemplateAdd } =
    postValuationTemplateAdd;

  const { isLoading: isLoadingUpdateValuation } = postValuationTemplateSave;

  const { data: dataValuationMetricsDefault } = getValuationMetricsDefault;

  const resetState = () => {
    if (!isUpdateMode) {
      setUpdatedValuationId(0);
      setUpdatedValuationName('');
      setInputedNotes('');
    }

    setValueSelectEPS(0);
    setValueEPS('');

    setValueSelectGrowth('CUSTOM');
    setValueGrowth('');

    setValueSelectMultiple(0);
    setValueMultiple('');

    setValuationModalCreate(false);
    setValuationModalUpdate(false);

    setValuationMetricsDisabled(true);
    setSeedDataValuationResult({
      target_price: '',
      current_price: '',
      margin_safety: '',
      consensus_high: '',
      consensus_low: '',
      consensus_medium: '',
      symbol: '',
    });

    setShowResultSection(false);
  };

  const getSeedData = () => {
    const dataResult = [
      {
        name: 'target_price',
        value: seedDataValuationResult.target_price,
      },
      {
        name: 'current_price',
        value: seedDataValuationResult.current_price,
      },
      {
        name: 'margin_safety',
        value: seedDataValuationResult.margin_safety,
      },
      {
        name: 'consensus_high',
        value: seedDataValuationResult.consensus_high,
      },
      {
        name: 'consensus_medium',
        value: seedDataValuationResult.consensus_medium,
      },
      {
        name: 'consensus_low',
        value: seedDataValuationResult.consensus_low,
      },
    ];

    const dataMetrics = {
      item: [
        {
          name: 'EPS',
          itemid: String(valueSelectEPS),
          value: valueEPS,
        },
        {
          name: 'GROWTH',
          itemid: String(valueSelectGrowth),
          value: valueGrowth,
        },
        {
          name: 'MULTIPLE',
          itemid: String(valueSelectMultiple),
          value: valueMultiple,
        },
      ],
      comp_id: stock.id,
    };

    return {
      metrics: dataMetrics,
      results: dataResult,
    };
  };

  const handleOnClickSave = (inputed) => {
    if (isUpdateMode) {
      // show modal update
      setValuationModalUpdate(true);
    } else {
      // show modal create
      setValuationModalCreate(true);
    }
    // set inputed notes
    setInputedNotes(inputed);
  };

  const handleCancelValuationModalCreate = () => {
    // hide modal create
    setValuationModalCreate(false);
  };

  const handleCancelValuationModalUpdate = () => {
    // hide modal update
    setValuationModalUpdate(false);
  };

  const handleSubmitValuationModalCreate = async (valueInput) => {
    const data = getSeedData();

    const requestBody = {
      company_id: stock.id,
      name: valueInput,
      metric: JSON.stringify(data.metrics),
      result: JSON.stringify(data.results),
      note: inputedNotes,
    };

    const add = await dispatch(effects.postValuationTemplateAdd(requestBody));

    // @ts-ignore
    const { error, data: resData, message } = add.payload;

    if (!error) {
      // Set alert
      handleErrorMessageAPI(message, SUCCESS_MESSAGE.ALERT_GREEN);

      // set update mode on
      setUpdateMode(true);

      // hide modal create
      setValuationModalCreate(false);

      // dispatch list valuation
      // HIT API getValuationTemplate
      dispatch(effects.getValuationTemplate());
      dispatch(effects.getValuationTemplateLoad(resData.id));

      // new valuation id
      setUpdatedValuationId(resData.id);
      // new valuation name
      setUpdatedValuationName(resData.name);
    }
  };

  const handleSubmitValuationModalUpdate = async () => {
    const data = getSeedData();

    const requestBody = {
      name: updatedValuationName,
      metric: JSON.stringify(data.metrics),
      result: JSON.stringify(data.results),
      note: inputedNotes,
    };

    const update = await dispatch(
      effects.postValuationTemplateSave({
        templateId: updatedValuationId,
        requestBody,
      }),
    );

    // @ts-ignore
    const { error, message } = update.payload;

    if (!error) {
      // hide modal update
      setValuationModalUpdate(false);

      handleErrorMessageAPI(message, SUCCESS_MESSAGE.ALERT_GREEN);

      // dispatch list valuation
      // HIT API getValuationTemplate
      dispatch(effects.getValuationTemplate());
      dispatch(effects.getValuationTemplateLoad(updatedValuationId));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (stock?.name) {
      const requestBody = {
        symbol: stock.name,
        eps_value: valueEPS,
        growth_value: valueGrowth,
        multiple_value: valueMultiple,
      };

      // dispatch post postValuationResult

      dispatch(effects.postValuationResult(requestBody));

      setShowResultSection(true);
    }
  };

  const handleSelectedValueStock = async (selectedStock) => {
    setStock(selectedStock);

    const queryString = {
      symbol: selectedStock.name,
    };

    // dispatch default metrics
    // @ts-ignore
    const getMetric = await dispatch(
      effects.getValuationMetricsDefault(queryString),
    );
    // @ts-ignore
    const { error, data } = getMetric.payload;

    // Set table upon success get company metrid
    if (!error) {
      const [eps, growth, multiple] = data;
      // index 0: EPS
      setValueSelectEPS(String(eps.id));
      setValueEPS(eps.value);

      // index 1: GROWTH
      setValueSelectGrowth(
        growth.id === '' || growth.id === 0 ? 'CUSTOM' : String(growth.id),
      );
      setValueGrowth(growth.value);

      // index 2: MULTIPLE
      setValueSelectMultiple(String(multiple.id));
      setValueMultiple(multiple.value);

      setValuationMetricsDisabled(false);
    }
  };

  useEffect(() => {
    const { isSubmitted, data: dataValuationResult } = postValuationResult;

    if (isSubmitted) {
      setSeedDataValuationResult({
        target_price: dataValuationResult.target_price,
        current_price: dataValuationResult.current_price,
        margin_safety: dataValuationResult.margin_safety,

        consensus_high: dataValuationResult.consensus_high,
        consensus_low: dataValuationResult.consensus_low,
        consensus_medium: dataValuationResult.consensus_medium,

        symbol: dataValuationResult.symbol,
      });
    }
  }, [postValuationResult]);

  const getResultData = (data: Array<any>, key: string) =>
    data.find((item) => item.name === key).value || 0;

  const getMetricsData = async (id: number) => {
    if (!stock?.name) {
      return '';
    }

    const res = await valuationApi.getValuationMetricPerID(stock.name, id);

    if (!res.data) {
      return '';
    }

    const { error, data } = res.data;

    if (error) {
      return '';
    }

    return data[0];
  };

  useEffect(() => {
    const { data: dataValuationDetail, isLoading } = getValuationTemplateLoad;

    if (Object.keys(dataValuationDetail).length > 0 && !isLoading) {
      const { valuation, metric, result, note } = dataValuationDetail;
      const { company, item } = metric;

      const queryString = {
        symbol: company.symbol,
      };

      if (isUpdateMode) {
        // dispatch default metrics
        dispatch(effects.getValuationMetricsDefault(queryString));

        setUpdatedValuationId(valuation.id);
        setUpdatedValuationName(valuation.name);
        const [eps, growth, multiple] = item;

        // Set table valuation metric
        setValueEPS(eps.value);
        setValueSelectEPS(eps.itemid);

        setValueGrowth(growth.value);
        setValueSelectGrowth(growth.itemid);

        setValueMultiple(multiple.value);
        setValueSelectMultiple(multiple.itemid);

        setStock({
          id: company.id,
          name: company.symbol,
        });

        setInputedNotes(note);

        setSeedDataValuationResult({
          target_price: getResultData(result, 'target_price'),
          current_price: getResultData(result, 'current_price'),
          margin_safety: getResultData(result, 'margin_safety'),
          consensus_high: getResultData(result, 'consensus_high'),
          consensus_low: getResultData(result, 'consensus_low'),
          consensus_medium: getResultData(result, 'consensus_medium'),
          symbol: company.symbol,
        });

        setShowResultSection(true);
        setValuationMetricsDisabled(false);
      } else {
        setValuationMetricsDisabled(true);
      }
    }
  }, [getValuationTemplateLoad, dispatch, isUpdateMode, setShowResultSection]);

  const handleOnSelectEPS = (value) => {
    setValueSelectEPS(value);

    // if select id same with default, change value to default
    if (
      dataValuationMetricsDefault?.length &&
      Number(value) === Number(dataValuationMetricsDefault[0].id)
    ) {
      setValueEPS(dataValuationMetricsDefault[0].value);
    } else {
      setValueEPS('0');
    }
  };

  const handleOnChangeEPS = (e) => {
    const inputedEPS = e.target.value.trim();

    setValueEPS(inputedEPS);

    // if inputed same with default, change select to custom
    if (
      dataValuationMetricsDefault?.length &&
      inputedEPS !== dataValuationMetricsDefault[0].value
    ) {
      setValueSelectEPS('CUSTOM');
    }
  };

  const handleOnSelectGrowth = async (value) => {
    setValueSelectGrowth(value);

    // if select id same with default, change value to default
    if (
      dataValuationMetricsDefault?.length >= 2 &&
      (value === dataValuationMetricsDefault[1].id ||
        (value === false && dataValuationMetricsDefault[1].id === ''))
    ) {
      setValueGrowth(dataValuationMetricsDefault[1].value);
      return;
    }

    if (value !== 'CUSTOM') {
      const data = await getMetricsData(value);
      setValueGrowth(data?.value || '');
      return;
    }

    setValueGrowth('0');
  };

  const handleOnChangeGrowth = (e) => {
    const inputedGrowth = e.target.value.trim();

    setValueGrowth(inputedGrowth);

    // if inputed same with default, change select to custom
    if (
      dataValuationMetricsDefault?.length >= 2 &&
      inputedGrowth !== dataValuationMetricsDefault[1].value
    ) {
      setValueSelectGrowth('CUSTOM');
    }
  };

  const handleOnSelectMultiple = async (value) => {
    setValueSelectMultiple(value);

    // if select id same with default, change value to default
    if (
      dataValuationMetricsDefault?.length >= 3 &&
      value === dataValuationMetricsDefault[2].id
    ) {
      setValueMultiple(dataValuationMetricsDefault[2].value);
      return;
    }

    if (value !== 'CUSTOM') {
      const data = await getMetricsData(value);
      setValueMultiple(data?.value || '');
      return;
    }
    setValueMultiple('0');
  };

  const handleOnChangeMultiple = (e) => {
    const inputedMultiple = e.target.value.trim();

    setValueMultiple(inputedMultiple);

    // if inputed same with default, change select to custom
    if (
      dataValuationMetricsDefault?.length >= 3 &&
      inputedMultiple !== dataValuationMetricsDefault[2].value
    ) {
      setValueSelectMultiple('CUSTOM');
    }
  };

  return (
    <div
      id="sw-valuation-form"
      style={{
        height: 'calc(100% - 40px)',
        overflowY: 'auto',
        padding: '0 10px 10px 10px',
        zIndex: 100,
      }}
    >
      <Text family="bold" mb="10px" mt="10px">
        Valuation Method
      </Text>
      <Select width="100%" heihgt="30px" defaultValue="PEM" mb="15px" getPopupContainer={(trigger) => trigger.parentElement}>
        <Select.Option value="PEM">Price to Earnings Method</Select.Option>
      </Select>
      <Text family="bold" mb="10px">
        Valuation Metric
      </Text>
      <form onSubmit={handleSubmit}>
        <Search
          searchValue={stock?.name || ''}
          handleSelectedValueStock={handleSelectedValueStock}
          isEdit={isUpdateMode}
          isClickAlertLog={false}
          onReset={resetState}
        />
        <div style={{ borderBottom: `1px solid ${color.gainsboro}` }}>
          <InputMetrics
            metrics={VALUATION_METRICS.EPS}
            selectValue={valueSelectEPS}
            value={valueEPS}
            disabled={isValuationMetricsDisabled}
            onChange={handleOnChangeEPS}
            onSelect={handleOnSelectEPS}
          />
          <InputMetrics
            metrics={VALUATION_METRICS.GROWTH}
            selectValue={valueSelectGrowth}
            value={valueGrowth}
            disabled={isValuationMetricsDisabled}
            onChange={handleOnChangeGrowth}
            onSelect={handleOnSelectGrowth}
          />
          <InputMetricMultiple
            value={valueMultiple}
            selectValue={valueSelectMultiple}
            disabled={isValuationMetricsDisabled}
            onChange={handleOnChangeMultiple}
            onSelect={handleOnSelectMultiple}
          />
        </div>
        <Button
          type="defaultHoverable"
          margin="15px 0"
          block
          round
          htmlType="submit"
        >
          <Text align="center" height="27px" color="inherit">
            Value
          </Text>
        </Button>
      </form>

      {/* result section */}
      {isShowResultSection && (
        <ValuationResult
          isUpdateMode={isUpdateMode}
          onClick={handleOnClickSave}
          dataValuationResult={seedDataValuationResult}
          notes={inputedNotes}
          isLoading={postValuationResult.isLoading}
        />
      )}

      {/* modal valuation create */}
      <ModalCreate
        isVisible={isValuationModalCreate}
        onCancel={handleCancelValuationModalCreate}
        onSubmit={handleSubmitValuationModalCreate}
        isLoading={isLoadingPostValuationTemplateAdd}
      />

      {/* modal valuation update */}
      <ValuationModalUpdate
        valuationName={updatedValuationName}
        visible={isValuationModalUpdate}
        onCancel={handleCancelValuationModalUpdate}
        onSubmit={handleSubmitValuationModalUpdate}
        isLoading={isLoadingUpdateValuation}
      />
    </div>
  );
};

export default ValuationForm;
